import style from "./style.scss";
import editorialStyle from "../../../components/editorial/style.scss";

import { Fragment } from "preact";
import { useState, useId, useCallback, useMemo, useEffect } from "preact/hooks";
import Link, { Button, affiliateMerchants } from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import Tags from "../../../components/tags";
import IconDown from "../../../components/icons/down";
import Author from "../../../components/author";
import EcoFlowCouponCode from "../components/coupon-code";
import Accordion from "../../../components/accordion";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import PhotoListItem from "../../../components/photo-list-item";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import FixedTable from "../../../components/fixed-table";
import Warning from "../../../components/warning";
import ImageSet from "../../../components/image-set";
import NoWrap from "../../../components/no-wrap";
import CompatibilityIcon from "./compatibility-icon";
import { lookupByUrl } from "../../../settings/route-data-lookup";
import { mergeClasses } from "../../../components/helpers";
import namedRoutes from "../../../settings/routes";

import { usePopup } from "../../../components/popup/context";
import Popup from "../../../components/popup";
import PopupLink from "../../../components/popup-link";

import AffiliateAd from "../../../components/affiliate-ad";

import extraBatteryData from "../../../data/extra-batteries/ecoflow";
import powerStationData from "../../../data/power-stations/ecoflow";
import { getGroupedData } from "../../power-systems/dataHelper";
import DataViewBattery from "../../power-systems/dataViewBattery";

import {
  VerificationLegend,
  useVerificationLegendModal,
} from "./verification-details";

const VerificationDetails = () => (
  <Fragment>
    <p>
      EcoFlow's product documentation and compatibility charts don't always
      provide every combination in one place. This guide consolidates official
      EcoFlow manuals, technical specifications, and manufacturer charts into a
      single reference.
    </p>
    <p>
      To ensure accuracy, I have my compatibility tables reviewed directly by
      EcoFlow internal contacts and product managers to verify individual
      compatibility claims and configurations. Working with these direct
      manufacturer contacts rather than general customer-support representatives
      ensures that specific connector types and adapter requirements are
      properly verified.
    </p>

    <h3>Compatibility Status Legend</h3>
    <VerificationLegend />

    <h3>Real-World Feedback & Findings</h3>
    <p>
      <strong>Have real-world compatibility findings to share?</strong> I would
      love to <Link href={namedRoutes.links.contact}>hear your feedback</Link>,
      whether it's a setup that works without official support or an issue with
      an officially compatible model.
    </p>

    <h3>Sources</h3>
    <ol style="font-size: 0.75em;">
      <li>
        <cite>
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://eu.ecoflow.com/products/delta-3-series-smart-extra-battery?variant=51349089812823"
          >
            EcoFlow Battery Compatibility Overview
          </Link>{" "}
          <sup>
            (
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://eu.ecoflow.com/cdn/shop/files/eu_28bc5344-5951-4efa-8016-4fa47839a3af.webp?v=1786533916"
            >
              image
            </Link>
            )
          </sup>{" "}
          <NoWrap>
            - <span class={style.textOpacity}>eu.ecoflow.com</span>
          </NoWrap>
        </cite>
      </li>
      <li>
        <cite>
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/products/delta-3-max-plus-smart-extra-battery?variant=55069632790601"
          >
            EcoFlow DELTA 3 Max Plus Smart Extra Battery
          </Link>{" "}
          <NoWrap>
            - <span class={style.textOpacity}>us.ecoflow.com</span>
          </NoWrap>
        </cite>
      </li>
      <li>
        <cite>
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://manuals.ecoflow.com/us/product/delta-3-max-plus-smart-extra-battery?lang=en_US"
          >
            EcoFlow DELTA 3 Max Plus Smart Extra Battery manual
          </Link>
        </cite>{" "}
        <NoWrap>
          - <span class={style.textOpacity}>manuals.ecoflow.com</span>
        </NoWrap>
      </li>
      <li>
        <cite>
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=54718699962441&view=d3m-121-d3mp"
          >
            EcoFlow DELTA 3 Max Plus Compatibility
          </Link>{" "}
          <sup>
            (
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/cdn/shop/files/D3MP-PC.png?v=1763616526&width=1200"
            >
              image
            </Link>
            )
          </sup>{" "}
          <NoWrap>
            - <span class={style.textOpacity}>us.ecoflow.com</span>
          </NoWrap>
        </cite>
      </li>
      <li>
        <cite>
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/products/delta-3-ultra-series-portable-power-station?variant=54718705008713&view=d3up-2605-new"
          >
            EcoFlow DELTA 3 Ultra Plus Compatibility
          </Link>{" "}
          <sup>
            (
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/cdn/shop/files/PC_54ceac35-b8cc-472f-bdb0-a0d02d96d063.png?v=1772767586&width=2000"
            >
              image
            </Link>
            )
          </sup>{" "}
          <NoWrap>
            - <span class={style.textOpacity}>us.ecoflow.com</span>
          </NoWrap>
        </cite>
      </li>
      <li>
        <cite>
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/products/delta-pro-ultra-x-smart-extra-battery?variant=54732348719177"
          >
            EcoFlow DELTA Pro Ultra Series Smart Extra Battery
          </Link>
        </cite>{" "}
        <NoWrap>
          - <span class={style.textOpacity}>us.ecoflow.com</span>
        </NoWrap>
      </li>
      <li>
        <cite>
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://www.ecoflow.com/us/power-kits-5kva-48v-power-system"
          >
            Power Kit Hub Compatibility
          </Link>{" "}
          <sup>
            (
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://websiteoss.ecoflow.com/upload/image/1744597361847/PC_PK.png"
            >
              image
            </Link>
            )
          </sup>{" "}
          <NoWrap>
            - <span class={style.textOpacity}>www.ecoflow.com</span>
          </NoWrap>
        </cite>
      </li>
      <li>
        <cite>
          <Link
            external
            href="https://us.ecoflow.com/products/river-3-plus-smart-extra-battery?variant=41636546216009"
          >
            EcoFlow RIVER 3 Plus Smart Extra Battery
          </Link>
        </cite>{" "}
        <NoWrap>
          - <span class={style.textOpacity}>us.ecoflow.com</span>
        </NoWrap>
      </li>
    </ol>

    <p>
      <strong>NOTE:</strong> An official EcoFlow graphic{" "}
      <sup>
        (
        <Link
          external
          nofollow
          href="https://eu.ecoflow.com/cdn/shop/files/pc__Compatibility_eu_ba6133cc-038d-4ba1-b42d-f19e37db2c26.png?v=1760088527"
        >
          example
        </Link>
        )
      </sup>{" "}
      widely shared across numerous sites and online communities incorrectly
      indicates that the DELTA Max Extra Battery is compatible with the DELTA 3
      and DELTA 3 1500. When I brought this discrepancy to EcoFlow’s attention
      during my review process, they confirmed the graphic contains an error and
      are working to correct it.
    </p>
  </Fragment>
);

function getProductRank(name, isBattery = false) {
  // DELTA Pro Ultra (Check first so it doesn't match "DELTA Pro")
  if (name.startsWith("DELTA Pro Ultra")) return 5;

  // DELTA Pro
  if (name.startsWith("DELTA Pro")) return 1;

  // DELTA 3
  if (name.startsWith("DELTA 3")) return 2;

  // DELTA 2
  if (name.startsWith("DELTA 2")) return 3;

  // Original DELTA Series
  if (name.startsWith("DELTA")) return 4;

  // TITAN Series
  if (name.startsWith("TITAN")) return 6;

  // Power Kits
  if (isBattery) {
    if (name.startsWith("5kWh LFP")) return 7;
    if (name.startsWith("2kWh LFP")) return 8;
  }
  if (name.startsWith("Power Kit")) {
    if (name.startsWith("Power Kits 5kVA")) {
      if (name.endsWith("100A")) return 7;
      return 8;
    }
    return 9;
  }

  // RIVER Series
  if (name.startsWith("RIVER")) {
    if (name.startsWith("RIVER 3")) return 10;
    if (!isBattery) {
      if (name.startsWith("RIVER 2")) return 11; // Doesn't exist for battery
    }
    return 12;
  }

  return 99;
}

function sortProductsComparator(a, b, isBattery = false) {
  const rankA = getProductRank(a.name, isBattery);
  const rankB = getProductRank(b.name, isBattery);

  if (rankA !== rankB) {
    return rankA - rankB;
  }
  return (b.capacity || 0) - (a.capacity || 0); // Descending capacity
}

// ---------------------------------------------------------------------------
// 1. DATA PREPARATION & CAPTION BYPASS
// ---------------------------------------------------------------------------

const powerStationGroupedData = getGroupedData(powerStationData, {
  getGroupName: (item) => {
    const isExpandable = item.expansion?.expandable ?? item.expandable;
    return isExpandable ? "expandable" : "nonExpandable";
  },
  groups: ["expandable", "nonExpandable"],
  sort: (a, b) => sortProductsComparator(a, b, false),
  collectCaptions: false,
});

const extraBatteryGroupedData = getGroupedData(extraBatteryData, {
  getGroupName: () => "all",
  groups: ["all"],
  sort: (a, b) => sortProductsComparator(a, b, true),
  collectCaptions: false,
});

const extraBatteryMap = Object.fromEntries(
  extraBatteryGroupedData.all.systems.map((battery) => [battery.name, battery])
);

const powerStationMap = Object.fromEntries(
  powerStationGroupedData.expandable.systems.map((station) => [
    station.name,
    station,
  ])
);

// ---------------------------------------------------------------------------
// 2. 3-STATE COMPATIBILITY LOGIC
// ---------------------------------------------------------------------------

const getCompatibilityStatus = (station, battery) => {
  // Check station side
  const stationList = station.expansion?.compatibleBatteries || [];
  const foundInStation = stationList.find((b) => {
    return b.name === battery.name;
  });

  if (foundInStation) {
    const isObject = typeof foundInStation === "object";
    return {
      type:
        isObject && foundInStation.type === "adapter" ? "adapter" : "direct",
      official: isObject ? Boolean(foundInStation.official) : false,
    };
  }

  return { type: "none", official: false };
};

const isStationBatteryCompatible = (station, battery) => {
  return getCompatibilityStatus(station, battery).type !== "none";
};

// ---------------------------------------------------------------------------
// 3. MATRIX HEADERS & MODALS
// ---------------------------------------------------------------------------

const batteryHeaders = extraBatteryGroupedData.all.systems.map((battery) => {
  const formattedName = battery.name
    .split(/(Smart Extra Battery|Extra Battery|Battery)/)
    .map((part, i) =>
      ["Smart Extra Battery", "Extra Battery", "Battery"].includes(part) ? (
        <Fragment key={i}>
          <br class={style.breakName} />
          {/* {part} */}
          {part === "Smart Extra Battery" ? "Extra Battery" : part}
        </Fragment>
      ) : (
        part
      )
    );

  return {
    name: battery.name,
    img: battery.img,
    formattedName,
  };
});

const deltaProUltraBatteryHeaders = (() => {
  if (!batteryHeaders?.length) return [];

  const getRank = (name = "") => {
    if (name.startsWith("DELTA Pro Ultra")) return 1;
    return 99; // Default rank for all others
  };

  return [...batteryHeaders].sort((a, b) => getRank(a.name) - getRank(b.name));
})();

const powerKitBatteryHeaders = (() => {
  if (!batteryHeaders?.length) return [];

  const getRank = (name = "") => {
    if (name.startsWith("5kWh LFP")) return 1;
    if (name.startsWith("2kWh LFP")) return 2;
    return 99; // Default rank for all others
  };

  return [...batteryHeaders].sort((a, b) => getRank(a.name) - getRank(b.name));
})();

const headerInfo = lookupByUrl(namedRoutes.ecoflow.batteryCompatibility);

// Helper function to update URL query params without triggering router scroll resets
const updateQueryParams = (stationNames, batteryNames) => {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);

  if (stationNames.length > 0) {
    params.set("system", stationNames.join(","));
  } else {
    params.delete("system");
  }

  if (batteryNames.length > 0) {
    params.set("battery", batteryNames.join(","));
  } else {
    params.delete("battery");
  }

  const query = params.toString().replace(/%2C/g, ",");
  const newUrl = query
    ? `${window.location.pathname}?${query}`
    : window.location.pathname;

  // Modifies URL in the address bar silently
  window.history.replaceState(null, "", newUrl);
};

const SelectItem = ({ item, onSelect, isSelected }) => {
  // Sync local state with prop initially, but allow local override on click
  const [localSelected, setLocalSelected] = useState(isSelected);

  const handleClick = () => {
    // Immediately trigger the visual update on this component
    setLocalSelected(!localSelected);

    // Wait 200ms so the user sees the visual selection before closing/updating
    setTimeout(() => {
      if (onSelect) {
        onSelect(item.name);
      }
    }, 100);
  };

  return (
    <button
      type="button"
      aria-pressed={localSelected}
      class={mergeClasses([
        style.popupItemButton,
        localSelected ? style.isSelected : null,
      ])}
      onClick={handleClick}
    >
      <div class={style.img}>
        <img src={item.img} alt={item.name} />
      </div>
      <div class={style.name}>{item.name}</div>
      <span class={style.popupSelectAction} aria-hidden="true" />
    </button>
  );
};

const LookupModal = ({
  modalTitleId,
  title,
  items,
  selectedItems,
  onSelect,
}) => (
  <Popup ariaLabelledBy={modalTitleId}>
    <div class={style.popupWrapper}>
      <Editorial>
        <h4 id={modalTitleId}>Select {title}</h4>

        <ul class={style.selectItemList} aria-label="Select a model">
          {items.map((item) => {
            const isSelected = selectedItems.includes(item.name);

            return (
              <li key={item.name} role="presentation">
                <SelectItem
                  item={item}
                  onSelect={onSelect}
                  isSelected={isSelected}
                />
              </li>
            );
          })}
        </ul>
      </Editorial>
    </div>
  </Popup>
);

const CompatibilityLookup = ({
  title,
  description,
  items = [],
  selectedItems = [],
  onSelect,
  position = "start",
}) => {
  const popup = usePopup();
  const modalTitleId = useId();
  const descriptionId = useId();

  const handleSelectItem = useCallback(
    (itemName) => {
      if (onSelect) {
        onSelect(itemName, position);
      }
      popup.hidePopup();
    },
    [onSelect, popup, position]
  );

  const openDetails = useCallback(
    (e) => {
      e?.preventDefault();
      popup.showPopup(
        <LookupModal
          modalTitleId={modalTitleId}
          title={title}
          items={items}
          selectedItems={selectedItems}
          onSelect={handleSelectItem}
        />
      );
    },
    [popup, modalTitleId, title, items, selectedItems, handleSelectItem]
  );

  return (
    <div class={style.insertButtonWrapper}>
      <div class={style.buttonWrapper}>
        <Button
          type="button"
          onClick={openDetails}
          aria-haspopup="dialog"
          aria-describedby={descriptionId}
          class={style.insertButton}
        >
          <span class={style.insertButtonContent}>
            {`${selectedItems.length > 0 ? "Add" : "Select"} ${title}`}
            <IconDown />
          </span>
        </Button>
      </div>

      {selectedItems.length < 1 && (
        <p
          id={descriptionId}
          class={mergeClasses([style.description, style.clickableDescription])}
          onClick={openDetails}
        >
          <span class={style.text}>{description}</span>
        </p>
      )}
    </div>
  );
};

const SingleItemDetailsModal = ({ station, battery, isOfficial }) => {
  const openLegendDetailsModal = useVerificationLegendModal();

  return (
    <Popup ariaLabel={`${station.name} + ${battery.name}`}>
      <div class={style.popupWrapper}>
        <Editorial>
          <DataViewBattery
            data={{
              systems: [
                {
                  ...station,
                  expansion: {
                    ...station.expansion,
                    compatibleBatteries:
                      station.expansion?.compatibleBatteries?.filter(
                        (b) => b.name === battery.name
                      ),
                  },
                },
              ],
              captions: [],
            }}
            batteryData={{
              [battery.name]: battery,
            }}
            headingLevel={3}
            includeUnofficialItems
            CompatibilityIcon={CompatibilityIcon}
            openLegendDetailsModal={openLegendDetailsModal}
            hideCompatibleHeader={isOfficial}
          />
        </Editorial>
      </div>
    </Popup>
  );
};

const ItemDetailsModal = ({
  item,
  type,
  batteryMap,
  stationMap,
  fullBatteryMap,
  fullStationMap,
}) => {
  const [showAll, setShowAll] = useState(false);
  const openLegendDetailsModal = useVerificationLegendModal();

  const isStation = type === "station";

  const scopedMap = isStation ? batteryMap : stationMap;
  const fullMap = isStation ? fullBatteryMap : fullStationMap;

  const currentMap = useMemo(() => {
    if (!showAll) {
      return scopedMap;
    }

    // Find only keys in fullMap that aren't already in scopedMap
    const missingKeys = Object.keys(fullMap || {}).filter(
      (key) => !(key in (scopedMap || {}))
    );

    // If nothing is missing, return scopedMap as-is
    if (missingKeys.length === 0) {
      return scopedMap;
    }

    // Append ONLY the missing items onto scopedMap (preserves top order + minimum allocations)
    const missingMap = {};
    for (const key of missingKeys) {
      missingMap[key] = fullMap[key];
    }

    return { ...scopedMap, ...missingMap };
  }, [showAll, scopedMap, fullMap]);

  const { currentCount, totalCount } = useMemo(() => {
    // Get the list of compatible battery/station IDs
    const compatibleList = isStation
      ? (item.expansion?.compatibleBatteries || []).map((b) => b?.name)
      : Object.values(fullMap || {})
          .filter((s) =>
            s?.expansion?.compatibleBatteries?.some(
              (b) => b?.name === item.name
            )
          )
          .map((s) => s.name);

    //  Count how many are in the current scoped view vs total
    const current = compatibleList.filter(
      (id) => id in (scopedMap || {})
    ).length;

    return { currentCount: current, totalCount: compatibleList.length };
  }, [item, isStation, fullMap, scopedMap]);

  const hasMoreItems = totalCount > currentCount;

  return (
    <Popup ariaLabel={item.name}>
      <div class={style.popupWrapper}>
        <Editorial>
          <DataViewBattery
            data={{ systems: [item], captions: [] }}
            batteryData={isStation ? currentMap : undefined}
            powerStationData={!isStation ? currentMap : undefined}
            headingLevel={3}
            CompatibilityIcon={CompatibilityIcon}
            openLegendDetailsModal={openLegendDetailsModal}
          />

          {hasMoreItems && (
            <div class={style.modalFooterReveal}>
              <Button
                secondary
                type="button"
                size="small"
                class={style.modalRevealBtn}
                onClick={() => setShowAll((prev) => !prev)}
              >
                {showAll
                  ? "Show Table Items Only"
                  : `Show All Compatible Items (${totalCount - currentCount} more)`}
              </Button>
            </div>
          )}
        </Editorial>
      </div>
    </Popup>
  );
};

const TableMatrixModal = ({ stations, title }) => {
  const [isScreenshotMode, setIsScreenshotMode] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      setIsScreenshotMode(params.has("screenshot"));
    }
  }, []);

  return (
    <Popup
      ariaLabel={title}
      fullWidth
      hideClose={isScreenshotMode}
      squareCorners={isScreenshotMode}
    >
      <div class={style.popupWrapperMatrix}>
        <Editorial>
          <CompatibilityMatrix
            caption={title}
            stations={stations}
            extraBatteryMap={extraBatteryMap}
            powerStationMap={powerStationMap}
            inPopup
          />
        </Editorial>
      </div>
    </Popup>
  );
};

// ---------------------------------------------------------------------------
// 4. COMPATIBILITY MATRIX WITH SCOPED MODALS
// ---------------------------------------------------------------------------

const CompatibilityMatrix = ({
  caption,
  stations,
  batteryHeaderList = batteryHeaders,
  allowList = [], // Array of battery IDs/names to bypass filtering (e.g., ['delta-pro-3-eb', 'delta-pro-eb'])
  extraBatteryMap,
  powerStationMap,
  inPopup = false,
}) => {
  const [isScreenshotMode, setIsScreenshotMode] = useState(false);
  const popup = usePopup();
  const openLegendDetailsModal = useVerificationLegendModal();

  const activeBatteryHeaders = useMemo(() => {
    return batteryHeaderList.filter((battery) => {
      // 1. Allow if explicitly specified in the allowList
      if (allowList.includes(battery.name)) {
        return true;
      }

      // 2. Otherwise, perform standard compatibility filter
      return stations.some((station) =>
        isStationBatteryCompatible(station, battery)
      );
    });
  }, [stations, batteryHeaderList, allowList]);

  // Construct a station map filtered specifically to the stations present in this matrix table
  const scopedStationMap = useMemo(() => {
    return Object.fromEntries(stations.map((st) => [st.name, st]));
  }, [stations]);

  // Construct a battery map filtered specifically to the batteries present in this matrix table
  const scopedBatteryMap = useMemo(() => {
    return Object.fromEntries(
      activeBatteryHeaders
        .map((b) => [b.name, extraBatteryMap[b.name]])
        .filter(([, item]) => Boolean(item))
    );
  }, [activeBatteryHeaders, extraBatteryMap]);

  // Determine which status types are present in the table
  const presentStatuses = useMemo(() => {
    const statuses = {
      direct: false,
      adapter: false,
      unofficial: false,
      incompatible: false,
    };

    for (const station of stations) {
      for (const battery of activeBatteryHeaders) {
        const status = getCompatibilityStatus(station, battery);

        if (status.type === "direct") {
          if (status.official) statuses.direct = true;
          else statuses.unofficial = true;
        } else if (status.type === "adapter") {
          if (status.official) statuses.adapter = true;
          else statuses.unofficial = true;
        } else {
          statuses.incompatible = true;
        }

        // Short-circuit early if all four legend items are confirmed present
        if (
          statuses.direct &&
          statuses.adapter &&
          statuses.unofficial &&
          statuses.incompatible
        ) {
          return statuses;
        }
      }
    }

    return statuses;
  }, [stations, activeBatteryHeaders]);

  const handleOpenSingleItem = useCallback(
    (station, battery, isOfficial) => {
      popup.showPopup(
        <SingleItemDetailsModal
          station={station}
          battery={battery}
          isOfficial={isOfficial}
        />
      );
    },
    [popup]
  );

  const handleOpenItem = useCallback(
    (itemKey, type) => {
      const item =
        type === "station"
          ? powerStationMap[itemKey]
          : extraBatteryMap[itemKey];

      if (!item) {
        return;
      }

      popup.showPopup(
        <ItemDetailsModal
          item={item}
          type={type}
          batteryMap={scopedBatteryMap}
          stationMap={scopedStationMap}
          fullBatteryMap={extraBatteryMap}
          fullStationMap={powerStationMap}
        />
      );
    },
    [
      popup,
      extraBatteryMap,
      powerStationMap,
      scopedBatteryMap,
      scopedStationMap,
    ]
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      setIsScreenshotMode(params.has("screenshot"));
    }
  }, []);

  return (
    <figure
      class={mergeClasses([
        style.matrixWrapper,
        inPopup ? style.inPopup : null,
        isScreenshotMode ? style.screenshot : null,
      ])}
    >
      <figcaption>{caption}</figcaption>
      <div class={style.matrix}>
        <FixedTable frameless={false} class={style.table}>
          <table>
            <caption>{caption}</caption>
            <colgroup>
              <col class={style.columnModel} />
              {activeBatteryHeaders.map((battery) => {
                return <col key={battery.name} class={style.column} />;
              })}
            </colgroup>
            <thead>
              <tr>
                <th class={editorialStyle.sticky} />
                {activeBatteryHeaders.map((battery) => {
                  return (
                    <th key={battery.name} scope="col">
                      <button
                        type="button"
                        class={style.headerButton}
                        onClick={() => handleOpenItem(battery.name, "battery")}
                      >
                        {battery.img && (
                          <img
                            src={battery.img}
                            alt={battery.name}
                            loading="lazy"
                          />
                        )}
                        <span class={style.name}>{battery.formattedName}</span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {stations.map((station) => {
                return (
                  <tr key={station.name} class={editorialStyle.noHover}>
                    <th scope="row" class={editorialStyle.sticky}>
                      <button
                        type="button"
                        class={style.headerButton}
                        onClick={() => handleOpenItem(station.name, "station")}
                      >
                        {station.img && (
                          <img
                            src={station.img}
                            alt={station.name}
                            loading="lazy"
                          />
                        )}
                        <span class={style.name}>{station.name}</span>
                      </button>
                    </th>
                    {activeBatteryHeaders.map((battery) => {
                      const status = getCompatibilityStatus(station, battery);

                      return (
                        <td key={`${station.name}-${battery.name}`}>
                          {status.type === "direct" ||
                          status.type === "adapter" ? (
                            <button
                              type="button"
                              class={style.cellButton}
                              onClick={() =>
                                handleOpenSingleItem(
                                  station,
                                  scopedBatteryMap[battery.name],
                                  status.official
                                )
                              }
                            >
                              {status.type === "direct" && (
                                <Fragment>
                                  <span class={style.cellText}>
                                    {status.official
                                      ? `${station.name} is officially compatible with ${battery.name}`
                                      : `${station.name} may work with ${battery.name}, but isn't officially supported`}
                                  </span>
                                  <CompatibilityIcon
                                    status={
                                      status.official ? "direct" : "unofficial"
                                    }
                                  />
                                </Fragment>
                              )}
                              {status.type === "adapter" && (
                                <Fragment>
                                  <span class={style.cellText}>
                                    {status.official
                                      ? `${station.name} is officially compatible with ${battery.name} using add-ons`
                                      : `${station.name} may work with ${battery.name} using add-ons, but isn't officially supported`}
                                  </span>
                                  <CompatibilityIcon
                                    status={
                                      status.official ? "adapter" : "unofficial"
                                    }
                                  />
                                </Fragment>
                              )}
                            </button>
                          ) : (
                            <Fragment>
                              <span class={style.cellText}>
                                {`${station.name} is not compatible with ${battery.name}`}
                              </span>
                              <CompatibilityIcon status="none" />
                            </Fragment>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </FixedTable>
      </div>
      {(presentStatuses.direct ||
        presentStatuses.adapter ||
        presentStatuses.unofficial) && (
        <ul aria-label="Compatibility Legend" class={style.legend}>
          {presentStatuses.direct && (
            <li>
              <button
                type="button"
                class={style.legendButton}
                onClick={openLegendDetailsModal}
                aria-label="View legend details"
              >
                <CompatibilityIcon status="direct" mini />{" "}
                <span aria-label="Compatible (Officially Verified)">
                  Compatible
                </span>
              </button>
            </li>
          )}
          {presentStatuses.adapter && (
            <li>
              <button
                type="button"
                class={style.legendButton}
                onClick={openLegendDetailsModal}
              >
                <CompatibilityIcon status="adapter" mini />{" "}
                <span aria-label="Compatible using adapter or accessory (Officially Verified)">
                  Needs add-on
                </span>
              </button>
              {/* Requires adapter/accessory */}
            </li>
          )}
          {presentStatuses.unofficial && (
            <li>
              <button
                type="button"
                class={style.legendButton}
                onClick={openLegendDetailsModal}
              >
                <CompatibilityIcon status="unofficial-bright" mini />{" "}
                <span aria-label="May work, not officially supported, use at your own risk">
                  May work
                </span>
              </button>
              {/* Unofficial, Caution */}
            </li>
          )}
          {presentStatuses.incompatible && (
            <li class={style.hideItem}>
              <button
                type="button"
                class={style.legendButton}
                onClick={openLegendDetailsModal}
              >
                <CompatibilityIcon status="none" mini />{" "}
                <span aria-label="Not supported">Incompatible</span>
              </button>
            </li>
          )}
        </ul>
      )}
      <div class={style.screenshotCaption}>
        See latest interactive version:{" "}
        <span class={style.screenshotCaptionFakeLink}>
          tysonsgearlab.com/efbc
        </span>
      </div>
    </figure>
  );
};

// ---------------------------------------------------------------------------
// 5. MAIN PAGE
// ---------------------------------------------------------------------------

const Page = () => {
  const popup = usePopup();
  const openLegendDetailsModal = useVerificationLegendModal();
  const modalTitleId = useId();

  const [selectedPowerStationNames, setSelectedPowerStationNames] = useState(
    () => {
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        const stationParam = params.get("system") || "";
        return stationParam
          .split(",")
          .map((s) => s.trim())
          .filter((name) => Boolean(name) && Boolean(powerStationMap[name]));
      }
      return [];
    }
  );

  const [selectedBatteryNames, setSelectedBatteryNames] = useState(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const batteryParam = params.get("battery") || "";
      return batteryParam
        .split(",")
        .map((b) => b.trim())
        .filter((name) => Boolean(name) && Boolean(extraBatteryMap[name]));
    }
    return [];
  });

  const togglePowerStation = useCallback((name, position = "end") => {
    setSelectedPowerStationNames((prev) => {
      const exists = prev.includes(name);
      let updated;
      if (exists) {
        updated = prev.filter((item) => item !== name);
      } else {
        updated = position === "start" ? [name, ...prev] : [...prev, name];
      }
      setSelectedBatteryNames((currentBatteries) => {
        updateQueryParams(updated, currentBatteries);
        return currentBatteries;
      });
      return updated;
    });
  }, []);

  const clearPowerStations = useCallback(() => {
    setSelectedPowerStationNames([]);
    setSelectedBatteryNames((currentBatteries) => {
      updateQueryParams([], currentBatteries);
      return currentBatteries;
    });
  }, []);

  const toggleBattery = useCallback((name, position = "end") => {
    setSelectedBatteryNames((prev) => {
      const exists = prev.includes(name);
      let updated;
      if (exists) {
        updated = prev.filter((item) => item !== name);
      } else {
        updated = position === "start" ? [name, ...prev] : [...prev, name];
      }
      setSelectedPowerStationNames((currentStations) => {
        updateQueryParams(currentStations, updated);
        return currentStations;
      });
      return updated;
    });
  }, []);

  const clearBatteries = useCallback(() => {
    setSelectedBatteryNames([]);
    setSelectedPowerStationNames((currentStations) => {
      updateQueryParams(currentStations, []);
      return currentStations;
    });
  }, []);

  const openPowerStationModal = useCallback(() => {
    popup.showPopup(
      <LookupModal
        modalTitleId={modalTitleId}
        title="Power Station Model"
        items={powerStationGroupedData.expandable.systems}
        selectedItems={selectedPowerStationNames}
        onSelect={(name) => {
          togglePowerStation(name, "end");
          popup.hidePopup();
        }}
      />
    );
  }, [popup, modalTitleId, selectedPowerStationNames, togglePowerStation]);

  const openBatteryModal = useCallback(() => {
    popup.showPopup(
      <LookupModal
        modalTitleId={modalTitleId}
        title="Battery Model"
        items={extraBatteryGroupedData.all.systems}
        selectedItems={selectedBatteryNames}
        onSelect={(name) => {
          toggleBattery(name, "end");
          popup.hidePopup();
        }}
      />
    );
  }, [popup, modalTitleId, selectedBatteryNames, toggleBattery]);

  const expandableStations = powerStationGroupedData.expandable.systems;

  const openFullMatrixModal = useCallback(
    (e) => {
      e.preventDefault();
      popup.showPopup(
        <TableMatrixModal
          title="EcoFlow Battery Compatibility Matrix"
          stations={expandableStations}
        />
      );
    },
    [popup, expandableStations]
  );

  const deltaProUltraStations = useMemo(
    () =>
      expandableStations.filter((s) => s.name.startsWith("DELTA Pro Ultra")),
    [expandableStations]
  );

  const deltaStations = useMemo(
    () =>
      expandableStations.filter(
        (s) =>
          s.name.startsWith("DELTA") && !s.name.startsWith("DELTA Pro Ultra")
      ),
    [expandableStations]
  );

  const titanStations = useMemo(
    () => expandableStations.filter((s) => s.name.startsWith("TITAN")),
    [expandableStations]
  );

  const powerKitStations = useMemo(
    () => expandableStations.filter((s) => s.name.startsWith("Power Kit")),
    [expandableStations]
  );

  const riverStations = useMemo(
    () => expandableStations.filter((s) => s.name.startsWith("RIVER")),
    [expandableStations]
  );

  const selectedPowerStations = useMemo(
    () =>
      selectedPowerStationNames
        .map((name) => powerStationMap[name])
        .filter(Boolean),
    [selectedPowerStationNames]
  );

  const selectedBatteries = useMemo(
    () =>
      selectedBatteryNames.map((name) => extraBatteryMap[name]).filter(Boolean),
    [selectedBatteryNames]
  );

  //https://us.ecoflow.com/cdn/shop/files/20251204-115825.png?v=1764826126
  //https://ecoflow-service-us-cdn.ecoflow.com/configurable/images/1782813278958/%E7%9F%A9%E9%98%B5Power%20Station-PC(1).webp?x-oss-process=image/resize,w_1920/format,webp/quality,Q_85/sharpen,100

  return (
    <div>
      <Head data={headerInfo} />
      <Hero
        short
        positionY={50}
        background="/images/backgrounds/ecoflow-battery-compatibility.webp"
        titlePrefix="EcoFlow"
        title="Battery Compatibility Guide"
        subTitle="Extra Battery & Expansion Options"
      />
      <Editorial>
        <Center>
          <Author
            published={headerInfo.datePublished}
            updated={headerInfo.dateModified}
          />

          <p>
            Use the <strong>interactive lookups</strong> and{" "}
            <strong>full compatibility table matrix</strong> below to compare
            and determine officially verified combinations for EcoFlow systems
            and expansion batteries. Explore by <strong>power station</strong>{" "}
            (solar generator) or <strong>extra battery</strong> model.
          </p>

          <AffiliateDisclosure />
        </Center>
        <Center>
          <SlugHeading tag="h2">
            Compatible Batteries by Power Station
          </SlugHeading>
          {/* <p>
            Find all compatible EcoFlow extra batteries for your power station,
            including port types, required adapters, expansion limits, and
            capacities:
          </p> */}

          <CompatibilityLookup
            title="Power Station Model"
            description="Select EcoFlow model to view full battery compatibility details, port types, required adapters, expansion limits, and
            capacities."
            items={powerStationGroupedData.expandable.systems}
            selectedItems={selectedPowerStationNames}
            onSelect={togglePowerStation}
            position="start"
          />

          <p class="caption">
            View{" "}
            <strong>
              <SlugLink text="Full Compatibility Comparison Tables">
                tables
              </SlugLink>
            </strong>{" "}
            or check by{" "}
            <strong>
              <SlugLink text="Compatible Power Stations by Battery">
                battery model
              </SlugLink>
            </strong>{" "}
            below
          </p>

          {/* IMPORTANT: div wrapper needed for client hydration mismatch */}
          <div>
            {selectedPowerStationNames.length > 0 && (
              <Fragment>
                <DataViewBattery
                  data={{ systems: selectedPowerStations, captions: [] }}
                  batteryData={extraBatteryMap}
                  headingLevel={3}
                  CompatibilityIcon={CompatibilityIcon}
                  openLegendDetailsModal={openLegendDetailsModal}
                />
                <div class={style.displayFooter}>
                  <button type="button" onClick={openPowerStationModal}>
                    Add Item
                  </button>
                  <button type="button" onClick={clearPowerStations}>
                    Clear Items
                  </button>
                </div>
              </Fragment>
            )}
          </div>

          <AffiliateAd brand="EcoFlow" size="billboard" />

          <EcoFlowCouponCode
            href="https://us.ecoflow.com/collections/extra-batteries"
            buttonText="Shop EcoFlow"
          />
        </Center>

        <Center>
          <SlugHeading tag="h2">
            Full Compatibility Comparison Tables
          </SlugHeading>
          {/* <p class="caption-left">
            Tables are interactive and scroll in both directions when needed.
            Blue and Green items are officially verified and supported by
            EcoFlow.
          </p> */}

          <Warning icon={false} blue>
            Select any table item to view full compatibility details, required
            adapters, and purchase links. Combinations marked as{" "}
            <CompatibilityIcon
              status="direct"
              mini
              style="margin-top:-4px; transform: scale(0.9);"
            />{" "}
            <strong>Compatible</strong> or{" "}
            <CompatibilityIcon
              status="adapter"
              mini
              style="margin-top:-4px; transform: scale(0.9);"
            />{" "}
            <strong>Needs add-on</strong> are{" "}
            <strong>officially verified by EcoFlow</strong>. See{" "}
            <PopupLink
              text="verification details"
              title="How I Verify EcoFlow Compatibility"
            >
              <VerificationDetails />
            </PopupLink>
            . Tables scroll horizontally and vertically when needed.
          </Warning>

          {deltaStations.length > 0 && (
            <Fragment>
              <SlugHeading tag="h3">DELTA Series Compatibility</SlugHeading>

              <CompatibilityMatrix
                caption="EcoFlow DELTA Series Battery Compatibility"
                stations={deltaStations}
                extraBatteryMap={extraBatteryMap}
                powerStationMap={powerStationMap}
              />
            </Fragment>
          )}

          {deltaProUltraStations.length > 0 && (
            <Fragment>
              <SlugHeading tag="h3">
                DELTA Pro Ultra Series Compatibility
              </SlugHeading>
              <CompatibilityMatrix
                caption="EcoFlow DELTA Pro Ultra Series Battery Compatibility"
                stations={deltaProUltraStations}
                allowList={[
                  "DELTA Pro 3 Smart Extra Battery",
                  "DELTA Pro Smart Extra Battery",
                ]}
                batteryHeaderList={deltaProUltraBatteryHeaders}
                extraBatteryMap={extraBatteryMap}
                powerStationMap={powerStationMap}
              />
            </Fragment>
          )}

          {powerKitStations.length > 0 && (
            <Fragment>
              <SlugHeading tag="h3">Power Kits Compatibility</SlugHeading>
              <CompatibilityMatrix
                caption="EcoFlow Power Kits Battery Compatibility"
                stations={powerKitStations}
                extraBatteryMap={extraBatteryMap}
                powerStationMap={powerStationMap}
                batteryHeaderList={powerKitBatteryHeaders}
              />
            </Fragment>
          )}

          {titanStations.length > 0 && (
            <Fragment>
              <SlugHeading tag="h3">TITAN Series Compatibility</SlugHeading>
              <CompatibilityMatrix
                caption="EcoFlow TITAN Series Battery Compatibility"
                stations={titanStations}
                extraBatteryMap={extraBatteryMap}
                powerStationMap={powerStationMap}
              />
            </Fragment>
          )}

          {riverStations.length > 0 && (
            <Fragment>
              <SlugHeading tag="h3">RIVER Series Compatibility</SlugHeading>
              <CompatibilityMatrix
                caption="EcoFlow RIVER Series Battery Compatibility"
                stations={riverStations}
                extraBatteryMap={extraBatteryMap}
                powerStationMap={powerStationMap}
              />
            </Fragment>
          )}

          <p>
            Access the{" "}
            <Link href="#" onClick={openFullMatrixModal}>
              Complete EcoFlow Battery Compatibility Chart
            </Link>{" "}
            (Interactive) or view as images below:
          </p>

          <ImageSet
            shadow={true}
            size={6}
            images={[
              {
                lg: "/images/products/ecoflow/compatibility/ecoflow-battery-compatibility-matrix-table-full.webp?v=3",
                alt: "EcoFlow Battery Compatibility Matrix Table",
              },
              {
                lg: "/images/products/ecoflow/compatibility/ecoflow-delta-and-delta-pro-series-battery-compatibility-matrix-table.webp?v=3",
                alt: "EcoFlow DELTA & DELTA Pro Series Battery Compatibility Matrix Table",
              },
              {
                lg: "/images/products/ecoflow/compatibility/ecoflow-delta-pro-ultra-series-battery-compatibility-matrix-table.webp?v=2",
                alt: "EcoFlow DELTA Pro Ultra Series Battery Compatibility Matrix Table",
              },
              {
                lg: "/images/products/ecoflow/compatibility/ecoflow-power-kits-battery-compatibility-matrix-table.webp?v=2",
                alt: "EcoFlow Power Kits Battery Compatibility Matrix Table",
              },
              {
                lg: "/images/products/ecoflow/compatibility/ecoflow-titan-series-battery-compatibility-matrix-table.webp",
                alt: "EcoFlow TITAN Battery Compatibility Matrix Table",
              },
              {
                lg: "/images/products/ecoflow/compatibility/ecoflow-river-series-battery-compatibility-matrix-table.webp?v=2",
                alt: "EcoFlow RIVER Series Battery Compatibility Matrix Table",
              },
            ]}
          />

          <p>
            <strong>All Model Details:</strong>
          </p>
          <Accordion
            right
            headingsAs="h3"
            items={[
              {
                heading: "Expandable Power Stations",
                content: (
                  <DataViewBattery
                    data={powerStationGroupedData.expandable}
                    batteryData={extraBatteryMap}
                    headingLevel={4}
                    CompatibilityIcon={CompatibilityIcon}
                    openLegendDetailsModal={openLegendDetailsModal}
                  />
                ),
              },
              {
                heading: "Non-Expandable Power Stations",
                content: (
                  <Fragment>
                    <p>
                      Some EcoFlow power stations lack a battery expansion port
                      and cannot expand their capacity:
                    </p>
                    <DataViewBattery
                      data={powerStationGroupedData.nonExpandable}
                      batteryData={extraBatteryMap}
                      headingLevel={4}
                      CompatibilityIcon={CompatibilityIcon}
                      openLegendDetailsModal={openLegendDetailsModal}
                    />
                  </Fragment>
                ),
              },
            ]}
          />
        </Center>
        <Center>
          <SlugHeading tag="h2">
            Compatible Power Stations by Battery
          </SlugHeading>
          <p>
            Already have an EcoFlow extra battery? Select your battery below to
            find every compatible EcoFlow power station, including required
            cables, adapters, and connector types:
          </p>

          <CompatibilityLookup
            title="Battery Model"
            description="Select model to view full power system compatibility details, port types, and adapters."
            items={extraBatteryGroupedData.all.systems}
            selectedItems={selectedBatteryNames}
            onSelect={toggleBattery}
            position="start"
          />

          <p class="caption">
            Check by{" "}
            <strong>
              <SlugLink text="Compatible Batteries by Power Station">
                power station model
              </SlugLink>
            </strong>{" "}
            above
          </p>

          {/* IMPORTANT: div wrapper needed for client hydration mismatch */}
          <div>
            {selectedBatteryNames.length > 0 && (
              <Fragment>
                <DataViewBattery
                  data={{ systems: selectedBatteries, captions: [] }}
                  powerStationData={powerStationMap}
                  headingLevel={3}
                  CompatibilityIcon={CompatibilityIcon}
                  openLegendDetailsModal={openLegendDetailsModal}
                />
                <div class={style.displayFooter}>
                  <button type="button" onClick={openBatteryModal}>
                    Add Item
                  </button>
                  <button type="button" onClick={clearBatteries}>
                    Clear Items
                  </button>
                </div>
              </Fragment>
            )}
          </div>

          <br />
          <p>
            <strong>All Battery Details:</strong>
          </p>
          <Accordion
            right
            headingsAs="h3"
            items={[
              {
                heading: "All Expansion Battery Models",
                content: (
                  <DataViewBattery
                    data={extraBatteryGroupedData.all}
                    powerStationData={powerStationMap}
                    headingLevel={4}
                    CompatibilityIcon={CompatibilityIcon}
                    openLegendDetailsModal={openLegendDetailsModal}
                  />
                ),
              },
            ]}
          />
        </Center>
        <Center>
          <h2>Connector Types & Cable Compatibility</h2>
          <p>
            Most EcoFlow systems rely on three primary extra battery connection
            types:
          </p>
          <ul>
            <li>
              <strong>XT150:</strong> Many DELTA series models
            </li>
            <li>
              <strong>4+8 (4P8):</strong> Select DELTA and DELTA Pro series
              models
            </li>
            <li>
              <strong>DELTA Pro Ultra:</strong> All DELTA Pro Ultra series
              models
            </li>
          </ul>
          <p>
            Other product lines use distinct connection methods, such as the
            Power Kit extra battery cables, the cableless Pogo Pin docking mount
            on select RIVER 3 models, or the legacy D-Sub port on the RIVER Pro.
          </p>
          <p>
            Matching connector types doesn't always mean two units are
            compatible. Surprisingly, even models with different connectors can
            sometimes work together using an adapter. All required adapters are
            accounted for in the compatibility data.
          </p>
        </Center>
        {/* <Center>
          <EcoFlowCouponCode
            href="https://us.ecoflow.com/collections/extra-batteries"
            buttonText="Shop Store"
          />
        </Center> */}
        <Center>
          <SlugHeading tag="h2">Accessories</SlugHeading>
          <p class="caption-left">
            Some of these cables and adapters are currently sold out. I’ve
            reached out to my EcoFlow contacts to see when they’ll be back in
            stock.
          </p>

          <h3>Battery Adapters</h3>
          <ul class="none">
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter?variant=39704596152393"
                img="/images/products/ecoflow/accessories/smart-generator-adapter.webp"
                title="Smart Generator Adapter"
                titleSuffix="(XT150 to 4+8)"
                description={
                  <Fragment>
                    For use with Extra Battery, Alternator Charger or Smart
                    Generator cables
                  </Fragment>
                }
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/ecoflow-extra-battery-expansion-adapter?variant=55507615645769"
                img="/images/products/ecoflow/accessories/extra-battery-expansion-adapter.webp"
                title="Extra Battery Expansion Adapter"
                titleSuffix="(DELTA 3 Max Plus & Ultra Plus)"
                description={
                  <Fragment>
                    Splitter providing two connections for batteries and/or an
                    alternator charger
                  </Fragment>
                }
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/power-dock?variant=41830215024713"
                img="/images/products/ecoflow/accessories/power-dock.webp"
                title="Power Dock"
                titleSuffix="(Power Kit)"
                description={
                  <Fragment>
                    Connect three batteries and/or two 48V DC appliances to{" "}
                    <Link
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/power-hub-5kva?variant=41830212206665"
                    >
                      5kVA Power Hub
                    </Link>{" "}
                    or{" "}
                    <Link
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/power-kits-power-hub?variant=40107761565769"
                    >
                      3.6kW Power Hub
                    </Link>
                  </Fragment>
                }
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
          </ul>

          <h3>Battery Cables</h3>
          <ul class="none">
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/extra-battery-cable"
                img="/images/products/ecoflow/accessories/delta-extra-battery-cable.webp"
                title="Extra Battery Cable"
                titleSuffix="(XT150)"
                description={
                  <Fragment>
                    Connects XT150 expansion ports found on many DELTA
                    systems{" "}
                  </Fragment>
                }
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/extra-battery-flat-link-cable?variant=41639152353353"
                img="/images/products/ecoflow/accessories/extra-battery-flat-link-cable-2m.webp"
                title="Extra Battery Flat Link Cable"
                titleSuffix="(4+8)"
                description={
                  <Fragment>
                    For use with DELTA Pro 3, DELTA Pro, DELTA 3 Ultra Plus, &
                    DELTA 3 Max Plus
                  </Fragment>
                }
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/delta-pro-extra-battery-cable?variant=39858307203145"
                img="/images/products/ecoflow/accessories/delta-pro-extra-battery-cable.webp"
                title="DELTA Pro Extra Battery Cable"
                titleSuffix="(4+8)"
                description={
                  <Fragment>
                    Connects the DELTA Pro to the DELTA Pro Smart Extra
                    Battery{" "}
                  </Fragment>
                }
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
            <li>
              <PhotoListItem
                affiliateData={affiliateMerchants.ecoflow}
                href="https://us.ecoflow.com/products/battery-connection-cable-delta-pro-ultra?variant=40768075661385"
                img="/images/products/ecoflow/accessories/battery-connection-cable-delta-pro-ultra-0_75m.webp"
                title="Battery Connection Cable"
                titleSuffix="(DELTA Pro Ultra)"
                description={
                  <Fragment>
                    For inverter-battery and battery-battery connections.
                  </Fragment>
                }
                buttonText="EcoFlow"
                buttonIcon="EcoFlow"
              />
            </li>
          </ul>

          {/* <p>
            <mark>TODO Keywords:</mark> LFP, LiFePO4, NMC, backwards compatible,
            forward compatible, expansion port, proprietary port, parallel,
            series, chain together, charging, stackable, capacity, expandable,
            expand, solar generator, whole-home backup power, RV, vanlife,
            Campervan, Overland, camping, Off-grid, cabin, emergency, power
            outage, firmware update, EcoFlow app, Bluetooth, monitoring,
            balance, equalizing, integration, setup, battery inverter, portable
            power bank, Emergency power supply (EPS), Uninterruptible Power
            Supply (UPS), head unit, base unit, add-on, extension, auxiliary,
            fridge backup
          </p> */}

          <h3>Joining Power Stations</h3>
          <p>
            You cannot use a second EcoFlow power station as an expansion
            battery to increase total capacity. Some DELTA Pro Series models
            support a hub or Smart Home Panel to combine their output power
            across multiple units.
          </p>
          <Accordion
            right
            headingsAs="h4"
            items={[
              {
                heading: "All Hubs",
                content: (
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/50-amp-hub?variant=40758561144905"
                        img="/images/products/ecoflow/accessories/50-amp-hub.webp"
                        title="50 Amp Hub"
                        description={
                          <Fragment>
                            Connect two DELTA Pro 3 or DELTA Pro Ultra units to
                            increase output
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/collections/delta-pro-series-accessories/products/smart-inlet-box?variant=54714821345353"
                        img="/images/products/ecoflow/accessories/smart-inlet-box-smart-home-panel-2-upgrade.webp"
                        title="Smart Inlet Box"
                        titleSuffix="(Smart Home Panel 2 Upgrade)"
                        description={<Fragment>More info coming soon</Fragment>}
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/double-voltage-hub-ecoflow-delta-pro?variant=39428708008009"
                        img="/images/products/ecoflow/accessories/double-voltage-hub-delta-pro.webp"
                        title="Double Voltage Hub"
                        titleSuffix="(DELTA Pro)"
                        description={
                          <Fragment>
                            Link two original DELTA Pro units and they will
                            distribute power evenly to anything plugged in
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/double-voltage-hub-power-input-output-delta-pro?variant=40768116129865"
                        img="/images/products/ecoflow/accessories/double-voltage-hub-power-input-output-delta-pro.webp"
                        title="Double Voltage Hub - Power Input/Output"
                        titleSuffix="(DELTA Pro)"
                        description={
                          <Fragment>
                            Connect two original DELTA Pro units with the{" "}
                            <Link
                              affiliateData={affiliateMerchants.ecoflow}
                              href="https://us.ecoflow.com/collections/delta-pro-series-accessories/products/smart-home-panel-2?variant=40767883575369"
                            >
                              Smart Home Panel 2
                            </Link>
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                  </ul>
                ),
              },
              {
                heading: "All  Smart Home Panels",
                content: (
                  <ul class="none">
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/collections/home-battery-smart-accessories/products/smart-home-panel-3?variant=54714805551177"
                        img="/images/products/ecoflow/home/smart-home-panel-3/front.webp"
                        title="EcoFlow Smart Home Panel 3"
                        description={
                          <Fragment>
                            Intelligent home subpanel for DELTA Pro Ultra X and
                            DELTA Pro Ultra
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/collections/home-battery-smart-accessories/products/smart-home-panel-2?variant=40767883575369"
                        img="/images/products/ecoflow/home/smart-home-panel-2/front.webp"
                        title="Smart Home Panel 2"
                        description={
                          <Fragment>
                            Intelligent home subpanel for DELTA Pro Ultra and
                            DELTA Pro 3
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>

                    <li>
                      <PhotoListItem
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://www.ecoflow.com/us/smart-home-panel"
                        img="/images/products/ecoflow/home/smart-home-panel/front.webp"
                        title="Smart Home Panel"
                        titleSuffix="(discontinued)"
                        description={
                          <Fragment>
                            Intelligent home subpanel for original DELTA
                            Pro{" "}
                          </Fragment>
                        }
                        buttonText="EcoFlow"
                        buttonIcon="EcoFlow"
                      />
                    </li>
                  </ul>
                ),
              },
            ]}
          />

          <h3>Alternator Charger Compatibility</h3>
          <p>
            EcoFlow alternator chargers can use either an extra battery port or
            a solar port. Plugging into a battery port occupies it, leaving one
            less port available for an extra battery.
          </p>
          <ul class="none">
            <li>
              <ReviewItem
                href={namedRoutes.ecoflow.altCharger.gen1Compatibility}
              />
            </li>
            <li>
              <ReviewItem
                href={namedRoutes.ecoflow.altCharger.gen2Compatibility}
              />
            </li>
          </ul>

          <h3>Other Systems</h3>
          <p>
            EcoFlow also offers specialized ecosystem products with limited
            cross-compatibility:{" "}
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-wave-2-add-on-battery2?variant=42077365436489"
            >
              WAVE 2 Add-on Battery
            </Link>
            ,{" "}
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-wave-3-add-on-battery?variant=54386591006793"
            >
              WAVE 3 Add-on Battery
            </Link>
            , and{" "}
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://uk.ecoflow.com/products/blade-robotic-lawn-mower?variant=48670887838035"
            >
              BLADE Smart Extra Battery
            </Link>
            . These can integrate with select compatible DELTA units.
            Additionally, compatible EcoFlow Smart Generators can connect to
            supported DELTA extra battery ports directly or via adapters for
            automated start-stop controls and direct DC charging.
          </p>

          <EcoFlowCouponCode
            href="https://us.ecoflow.com/collections/extra-batteries"
            buttonText="Shop EcoFlow"
          />

          <Accordion
            right
            headingsAs="h2"
            items={[
              {
                heading: "How I Verify EcoFlow System Compatibility",
                content: <VerificationDetails />,
              },
            ]}
          />

          <br />
          <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
        </Center>
        <Center>
          <h3>Related</h3>
          <ul class="none">
            <li>
              <ReviewItem href={namedRoutes.ecoflow.vanlife} />
            </li>
            <li>
              <ReviewItem href={namedRoutes.alternatorChargers} />
            </li>
            <li>
              <ReviewItem href={namedRoutes.inventory.vanlife} />
            </li>
          </ul>

          <br />
        </Center>
        <Questions />
        <Center>
          <Author
            author
            published={headerInfo.datePublished}
            updated={headerInfo.dateModified}
          />
        </Center>
      </Editorial>
    </div>
  );
};

export default Page;
